import type { DetectorOptions } from 'i18next-browser-languagedetector'
import { atom, getDefaultStore } from 'jotai'
import Cookies from 'js-cookie'
import { withBase, withoutBase } from 'ufo'
import {
  DEFAULT_LOCALE,
  type KnownLocaleCodes,
  LOCALE_ALIASES,
  LOCALE_COOKIE_KEY,
  type SupportedLocaleCodes,
} from 'src/i18n/constants'
import {
  isLocaleAlias,
  isSupportedLocale,
  normalizeLocale,
} from 'src/i18n/utils'

const store = getDefaultStore()

export const localeAtom = atom<SupportedLocaleCodes>(DEFAULT_LOCALE)
export const getLocale = () => store.get(localeAtom)
// oxfmt-ignore
export const setLocale = (value: SupportedLocaleCodes) => store.set(localeAtom, value)

/**
 * Parses and resolves a locale from a URL.
 *
 * Supported methods of specifying locale:
 * 1. **Path prefix**: `/zh-cn/some-page`
 *
 * Resolution priority (highest to lowest):
 * 1. Supported locale in path prefix
 * 2. Alias in path prefix
 *
 * @param fromUrl - The URL to parse. Defaults to `window.location.href` if omitted.
 *
 * @returns An object containing:
 * - `path`: The original first path segment, preserved if it was a supported
 *   locale or a recognized alias. Used by callers (e.g., `setLocaleToUrl`) to
 *   correctly replace the path prefix when changing locales.
 * - `resolved`: The final resolved locale code, or `undefined` if no valid
 *   locale could be determined from the URL.
 *
 * @example
 * // URL: https://example.com/zh/artwork?foo=bar
 * resolveLocaleFromUrl()
 * // => { path: 'zh', resolved: 'zh' }
 *
 * @example
 * // URL: https://example.com/kr/artwork (kr is alias for ko)
 * resolveLocaleFromUrl()
 * // => { path: 'kr', resolved: 'ko' }
 */
export function resolveLocaleFromUrl(fromUrl?: string) {
  const url = new URL(fromUrl ?? window.location.href)
  const pathSegment = url.pathname.split('/')[1] || undefined
  const pathLower = pathSegment?.toLowerCase()

  const pathAlias = isLocaleAlias(pathLower) && LOCALE_ALIASES[pathLower]

  const resolved = isSupportedLocale(pathLower)
    ? pathLower
    : pathAlias || undefined

  return {
    // Keep original path segment if it's either supported or an alias
    path: isSupportedLocale(pathLower) || pathAlias ? pathSegment : undefined,
    resolved,
  }
}

export function setLocaleToUrl(locale: SupportedLocaleCodes) {
  const { path, resolved } = resolveLocaleFromUrl()
  const url = new URL(window.location.href)
  if (resolved !== locale) {
    url.pathname = withBase(withoutBase(url.pathname, `/${path}`), `/${locale}`)
  }
  if (window.location.href !== url.href) {
    // Lang basename is set on react router initialization,
    // so we need to reload the page when changing the lang
    window.location.href = url.href
  }
}

/**
 * Syncs the locale from localStorage to a cookie so the server can read it on
 * subsequent requests. Aliases (e.g. `kr`) are resolved to their canonical
 * form before writing.
 */
export function migrateLocalStorageToCookie() {
  if (typeof window === 'undefined') return
  if (typeof localStorage === 'undefined') return

  const stored = localStorage.getItem('i18nextLng')
  if (!stored) return

  const lang = stored.toLowerCase()
  const normalized = normalizeLocale(lang)

  if (normalized) {
    setLocaleToCookies(normalized)
    localStorage.removeItem('i18nextLng')
  }
}

migrateLocalStorageToCookie()

export function getLanguageDetectorOptions(): DetectorOptions {
  return {
    // Cookie before path: gives CSR the same locale source as SSR; navigator and localStorage skipped.
    order: ['cookie', 'path'],
    lookupCookie: 'locale',
    // Disable all caching — we use the cookie (set by migrateLocalStorageToCookie)
    // as the source of truth, not the i18next localStorage cache.
    caches: [],
  }
}

export function setLocaleToCookies(value: KnownLocaleCodes) {
  return Cookies.set(LOCALE_COOKIE_KEY, value, {
    domain: window.location.hostname.endsWith('pixai.art')
      ? '.pixai.art'
      : undefined,
    expires: 365,
    path: '/',
    sameSite: 'Lax',
  })
}
